import { fabric } from 'fabric';
import AnalyticsBehaviors from './AnalyticsBehaviors';
import CollaborationBehaviors from './CollaborationBehaviors';
import ExternalContentBehaviors from './ExternalContentBehaviors';
import FrameMemberBehaviors from './FrameMemberBehaviors';
import LockBehaviors from './LockBehaviors';
import ReadOnlyBehaviors from './ReadOnlyBehaviors';
import SmartGuideBehaviors from './SmartGuideBehaviors';
import CommentableBehaviors from './CommentableBehaviors';
import EmbeddedContentBehaviors, {
  type EmbeddedContentBehaviorsInterface,
} from './EmbeddedContentBehaviors';
import {
  type AttachmentPoint,
  type Connectable,
  type ConnectControlDefinition,
} from '../types';
import { standardInboundConnectControlDefinition } from '../utils/connectors';
import ConnectorAttachmentBehaviors from './ConnectorAttachmentBehaviors';
import ToolEditEmbeddedAssistantArtifact from '../tools/ToolEditEmbeddedAssistantArtifact';

interface EmbeddedAssistantArtifact
  extends fabric.Object,
    CommentableBehaviors,
    CollaborationBehaviors,
    FrameMemberBehaviors,
    ConnectorAttachmentBehaviors,
    LockBehaviors,
    ReadOnlyBehaviors,
    SmartGuideBehaviors,
    EmbeddedContentBehaviorsInterface {}

@AnalyticsBehaviors
@ReadOnlyBehaviors
@ExternalContentBehaviors
@SmartGuideBehaviors
@CollaborationBehaviors
@LockBehaviors
@CommentableBehaviors
@FrameMemberBehaviors
@ConnectorAttachmentBehaviors
@EmbeddedContentBehaviors
class EmbeddedAssistantArtifact extends fabric.Object implements Connectable {
  type = 'embeddedAssistantArtifact';

  artifactToken: string;
  artifactVersion?: string;
  artifactTitle?: string;

  serializedKeys = ['artifactToken', 'artifactVersion'];

  constructor(options) {
    super(options);

    this.initializeLockBehaviors(options.locked);
    this.initializeSmartGuides();
    this.initializeFrameMember();
    this.initializeConnectorAttachments();
    this.initializeEmbeddedContentBehaviors();
  }

  static possibleAttachmentPoints: AttachmentPoint[] = Object.keys(
    EmbeddedAssistantArtifact.connectControlDefinition()
  ) as AttachmentPoint[];

  static connectControlDefinition() {
    return standardInboundConnectControlDefinition(false);
  }

  connectControlDefinition(): ConnectControlDefinition {
    return EmbeddedAssistantArtifact.connectControlDefinition();
  }

  attachmentPoints(): AttachmentPoint[] {
    return EmbeddedAssistantArtifact.possibleAttachmentPoints;
  }

  tools() {
    return [ToolEditEmbeddedAssistantArtifact];
  }

  get artifactUrl() {
    return `/assistant/shared_artifacts/${this.artifactToken}/${
      this.artifactVersion ?? '1'
    }`;
  }

  get externalContentUrl() {
    return this.artifactUrl;
  }

  protected get interactionMessage() {
    return 'Double click to interact with the artifact';
  }
}

export default EmbeddedAssistantArtifact;
